// MaturaTrapezoidAreasDiagram.js
// Trapez ABCD, |AB| = k·|CD|, przekątne AC i BD przecinają się w punkcie E.
// Rysunek do zadania maturalnego o polach trójkątów.
// Parametr k steruje stosunkiem długości podstaw (2 lub 3).

import React from "react";
import { InlineMath } from "react-katex";

export default function MaturaTrapezoidAreasDiagram({
  size = 360,
  strokeW = 2.4,
  k = 2, // stosunek |AB| : |CD| (domyślnie 2)
  className,
  style,
}) {
  const W = size;
  const H = Math.round(size * 0.45);

  const padX = 36;
  const padBottom = 32;
  const padTop = 32;

  const baseY = H - padBottom;
  const topY = padTop;

  // dolna podstawa AB – na całej dostępnej szerokości
  const bottomWidth = W - 2 * padX;
  const Ax = padX;
  const Bx = Ax + bottomWidth;

  // górna podstawa CD – krótsza, długość w proporcji 1/k
  const topWidth = bottomWidth / k;

  // NIE wycentrowana – lekko przesunięta, żeby trapez nie był równoramienny
  const minLeft = Ax;
  const maxLeft = Bx - topWidth;

  // trochę inne przesunięcie dla k = 2 i k = 3, żeby zawsze wyglądał „skośnie”
  const shiftFactor = k === 2 ? 0.25 : 0.6; // 0 = nad A, 1 = nad B
  const Dx = minLeft + (maxLeft - minLeft) * shiftFactor;
  const Cx = Dx + topWidth;

  const A = { x: Ax, y: baseY };
  const B = { x: Bx, y: baseY };
  const D = { x: Dx, y: topY };
  const C = { x: Cx, y: topY };

  // punkt E – przecięcie AC i BD (do podpisu)
  function intersect(p1, p2, p3, p4) {
    const { x: x1, y: y1 } = p1;
    const { x: x2, y: y2 } = p2;
    const { x: x3, y: y3 } = p3;
    const { x: x4, y: y4 } = p4;

    const A1 = y2 - y1;
    const B1 = x1 - x2;
    const C1 = A1 * x1 + B1 * y1;

    const A2 = y4 - y3;
    const B2 = x3 - x4;
    const C2 = A2 * x3 + B2 * y3;

    const denom = A1 * B2 - A2 * B1 || 1;
    const x = (B2 * C1 - B1 * C2) / denom;
    const y = (A1 * C2 - A2 * C1) / denom;
    return { x, y };
  }

  const E = intersect(A, C, B, D);

  const COLORS = {
    edges: "currentColor",
    diag1: "#8b5cf6", // AC
    diag2: "#10b981", // BD
    area1: "#8b5cf6", // BCE
    area2: "#10b981", // AED
    labels: "currentColor",
  };

  const LatexLabel = ({
    x,
    y,
    math,
    anchor = "middle",
    width = 34,
    height = 22,
    fontSize = 15,
  }) => {
    const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
    const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";

    return (
      <foreignObject x={boxX} y={y - height / 2} width={width} height={height}>
        <div style={{ color: COLORS.labels, fontSize, lineHeight: 1.15, textAlign }}>
          <InlineMath math={String(math)} />
        </div>
      </foreignObject>
    );
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{
        display: "block",
        margin: "0 auto",
        width: "100%",
        height: "auto",
        maxWidth: W,
        overflow: "visible",
        ...style,
      }}
    >
      {/* półprzezroczyste trójkąty z treści zadania */}
      <polygon
        points={`${B.x},${B.y} ${C.x},${C.y} ${E.x},${E.y}`}
        fill={COLORS.area1}
        fillOpacity={0.16}
        stroke="none"
      />
      <polygon
        points={`${A.x},${A.y} ${E.x},${E.y} ${D.x},${D.y}`}
        fill={COLORS.area2}
        fillOpacity={0.16}
        stroke="none"
      />

      {/* krawędzie trapezu */}
      <line
        x1={A.x}
        y1={A.y}
        x2={B.x}
        y2={B.y}
        stroke={COLORS.edges}
        strokeWidth={strokeW}
        strokeLinecap="round"
      />
      <line
        x1={D.x}
        y1={D.y}
        x2={C.x}
        y2={C.y}
        stroke={COLORS.edges}
        strokeWidth={strokeW}
        strokeLinecap="round"
      />
      <line
        x1={A.x}
        y1={A.y}
        x2={D.x}
        y2={D.y}
        stroke={COLORS.edges}
        strokeWidth={strokeW}
        strokeLinecap="round"
      />
      <line
        x1={B.x}
        y1={B.y}
        x2={C.x}
        y2={C.y}
        stroke={COLORS.edges}
        strokeWidth={strokeW}
        strokeLinecap="round"
      />

      {/* przekątne */}
      <line
        x1={A.x}
        y1={A.y}
        x2={C.x}
        y2={C.y}
        stroke={COLORS.diag1}
        strokeWidth={strokeW}
        strokeLinecap="round"
      />
      <line
        x1={B.x}
        y1={B.y}
        x2={D.x}
        y2={D.y}
        stroke={COLORS.diag2}
        strokeWidth={strokeW}
        strokeLinecap="round"
      />

      {/* etykiety A,B,C,D,E */}
      <LatexLabel
        x={A.x - 8}
        y={A.y + 17}
        math="A"
        anchor="end"
      />
      <LatexLabel
        x={B.x + 8}
        y={B.y + 17}
        math="B"
        anchor="start"
      />
      <LatexLabel
        x={C.x + 8}
        y={C.y - 8}
        math="C"
        anchor="start"
      />
      <LatexLabel
        x={D.x - 8}
        y={D.y - 8}
        math="D"
        anchor="end"
      />
      <LatexLabel x={E.x + 6} y={E.y + 4} math="E" anchor="start" width={28} />
    </svg>
  );
}
